import { CollapsibleContent, cn, Icon } from "@qw/ui";

import {
  ChatMessageToolInvocation,
  ChatMessageToolInvocationHeader,
  ChatMessageToolInvocationTitle,
  ChatMessageToolInvocationToggleButton,
} from "./chat-message-tool-invocation";

const CHAT_GENERATION_ERROR_TITLE = "Alby hit an error while generating this response.";

export interface ChatErrorDisclosureProps {
  className?: string;
  detail?: string | null;
  title?: string;
}

export const ChatErrorDisclosure = ({
  className,
  detail,
  title = CHAT_GENERATION_ERROR_TITLE,
}: ChatErrorDisclosureProps) => {
  const normalizedDetail = normalizeErrorDetail(detail);

  return (
    <ChatMessageToolInvocation className={cn("text-destructive", className)}>
      <ChatMessageToolInvocationHeader>
        <Icon icon="error" className="text-destructive" />
        <ChatMessageToolInvocationTitle tooltip={title}>{title}</ChatMessageToolInvocationTitle>
        {normalizedDetail && <ChatMessageToolInvocationToggleButton />}
      </ChatMessageToolInvocationHeader>

      {normalizedDetail && (
        <CollapsibleContent className="rounded-md border border-border bg-muted p-2 mt-1 text-xs leading-5 text-muted-foreground font-mono whitespace-pre-wrap">
          <pre className="whitespace-pre-wrap break-words overflow-x-auto">{normalizedDetail}</pre>
        </CollapsibleContent>
      )}
    </ChatMessageToolInvocation>
  );
};

export const getChatErrorDetail = (error: Error | string | null | undefined): string | null => {
  const message = typeof error === "string" ? error : error?.message;
  if (!message?.trim()) {
    return null;
  }

  const parsed = parseJsonObject(message);
  if (!parsed) {
    return message;
  }

  const details = [
    getReadableProperty(parsed, "error"),
    getReadableProperty(parsed, "message"),
    getReadableProperty(parsed, "details"),
  ].filter((value, index, values): value is string => Boolean(value && values.indexOf(value) === index));

  return details.length > 0 ? details.join("\n\n") : JSON.stringify(parsed, null, 2);
};

const normalizeErrorDetail = (detail: string | null | undefined): string | null => {
  const trimmed = detail?.trim();
  if (!trimmed || trimmed === CHAT_GENERATION_ERROR_TITLE) {
    return null;
  }

  return trimmed;
};

const parseJsonObject = (value: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

const getReadableProperty = (value: Record<string, unknown>, key: string): string | null => {
  const property = value[key];
  if (typeof property === "string" && property.trim()) {
    return property.trim();
  }

  if (!property || typeof property !== "object" || Array.isArray(property)) {
    return null;
  }

  const nested = property as Record<string, unknown>;
  return (
    getReadableProperty(nested, "message") ??
    getReadableProperty(nested, "error") ??
    getReadableProperty(nested, "details")
  );
};
